<template>
  <div class="customers data--cards">
    <div class="page__header">
      <div class="page__title">
        {{ $t(`pageTitles.${"leads"}`) }}
        
      </div>
      <div class="page__right"> 
         
        <div class="selected-filters" v-if="selectedTeamIds.length > 0 || Object.keys(searchLeadForms).length > 0">
          <div v-for="teamId in selectedTeamIds" :key="`team${teamId}`" class="selected-filters__filter">
            <span>Team: {{ getTeamDisplayName(teamId) }}</span>
            <div class="selected-filters__remove" @click="removeTeam(teamId)"></div>
          </div>

          <div class="selected-filters__filter" v-for="statusId in leadStatuses" :key="`status${statusId}`">
            <span>Status: {{
              (searchLeadForms.customer_sub_statuses && searchLeadForms.customer_sub_statuses.find(item => item.id ==
                statusId))
                ? searchLeadForms.customer_sub_statuses.find(item => item.id == statusId).name
                : '...'
            }}</span>
            <div class="selected-filters__remove" @click="removeLeadStatus(statusId)"></div>
          </div>

          <div class="selected-filters__filter" v-for="categoryId in leadCategories" :key="`category${categoryId}`">
            <span>Category: {{
              (searchLeadForms.branchCategories && searchLeadForms.branchCategories.find(item => item.id ==
                categoryId))
                ? searchLeadForms.branchCategories.find(item => item.id == categoryId)[`name`]
                : '...'
            }}</span>
            <div class="selected-filters__remove" @click="removeLeadCategory(categoryId)"></div>
          </div>

          <div class="selected-filters__filter" v-for="agentId in selectedAgents" :key="`agent${agentId}`">
            <span>Agent: 
              {{
                (searchLeadForms.agents && searchLeadForms.agents.find(a => a.id == agentId))
                  ? searchLeadForms.agents.find(a => a.id == agentId).first_name + ' ' +
                  searchLeadForms.agents.find(a => a.id == agentId).last_name
                  : 'Agent ' + agentId
              }}
            </span>
            <div class="selected-filters__remove" @click="removeAgent(agentId)"></div>
          </div>

          <div class="selected-filters__filter" v-for="referralId in leadReferrals" :key="`referral${referralId}`">
            <span>Referral: {{
              (searchLeadForms.referrals && searchLeadForms.referrals.find(item => item.id == referralId))
                ? searchLeadForms.referrals.find(item => item.id == referralId).name
                : 'Referral ' + referralId
            }}</span>
            <div class="selected-filters__remove" @click="removeLeadReferral(referralId)"></div>
          </div>

          <div class="selected-filters__filter" v-if="deadlineFrom || deadlineTo">
            <span>Deadline: <br>{{ formatDateRange(deadlineFrom, deadlineTo) }}</span>
            <div class="selected-filters__remove" @click="clearDeadline()"></div>
          </div>

          <div class="selected-filters__filter" v-if="zipFrom || zipTo">
            <span>{{ $t('searchCustomerFields.zip') }}: {{ formatRange(zipFrom, zipTo) }}</span>
            <div class="selected-filters__remove" @click="clearZip()"></div>
          </div>
        </div>
        <form class="search search--filter" @submit.prevent="searchLeads()">
          <div class="search__wrapper">
            <input class="search__search" ref="search" :class="{ 'search__search--active': searchKeywords }" type="text"
              v-model="searchKeywords" />
            <div class="search__icon"></div>
            <div class="selected-filters__filter search__placeholder" v-if="searchPlaceholder">
              <div class="search__placeholder-content">
                {{ searchPlaceholder }}
                <div class="selected-filters__remove" @click="removeSearch()"></div>
              </div>
            </div>
            <div class="search__splitter"></div>
          </div>
          <div class="search__filter" ref="filterToggler" @click="showSearchFilter = !showSearchFilter"></div>
          <SearchFilter v-if="showSearchFilter" :filterToggler="filterToggler" :searchType="'lead'"
            :toggleSearchFilter="(state) => {
              showSearchFilter = state;
            }
              " />
        </form> 

        <button v-if="canRunLeadBulkAction" type="button" class="button button--default import-button"
          @click="showLeadImportModal">
          Import Leads
        </button>

        <button v-if="canRunLeadBulkAction" type="button" class="button button--default transfer-button"
          @click="openTransferModal" :disabled="selectedCustomerIds.length === 0">
          Transfer{{ selectedCustomerIds.length > 0 ? ` (${selectedCustomerIds.length})` : '' }}
        </button>

        <button v-if="canCreateLead" type="button" class="button button--default add-button page__action--compact"
          @click="showCustomerLeadModal">
          <div class="button__icon"></div>
        </button>

      </div>
    </div>
    <div class="data_content">
      <Loading v-if="leadsLoading" />
      <div v-else-if="!canAccessLeads" style="height: 80vh;">
        <NoData type="no-permission" />
      </div>
      <NoData v-else-if="leadCollectionError?.status === 403" type="no-permission" />
      <LeadsTable v-else ref="leadsTable" :customers="leadCustomersPerPage.data || []" @refresh="searchLeads" @selection-changed="handleSelectionChange" />
    </div>
    <Pagination v-if="!leadsLoading && leadCustomersPerPage.data && leadCustomersPerPage.data.length > 0" :limit="3" :data="leadCustomersPerPage" @pagination-change-page="getResults"></Pagination>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import LeadsTable from "@/components/LeadsTable";
import Loading from "@/partials/Loading";
import SearchFilter from "@/partials/SearchFilter";
import NoData from "@/partials/NoData";

const store = useStore();
const route = useRoute();
const router = useRouter();

const searchPlaceholder = ref("");
const searchKeywords = ref("");
const showSearchFilter = ref(false);
const selectedTeamIds = ref([]);
const selectedTeamId = computed(() => selectedTeamIds.value.length > 0 ? selectedTeamIds.value[0] : null);
const selectedAgents = ref([]);
const leadStatuses = ref([]);
const leadCategories = ref([]);
const leadReferrals = ref([]);
const deadlineFrom = ref("");
const deadlineTo = ref("");
const zipFrom = ref("");
const zipTo = ref("");
const selectedCustomerIds = ref([]);

// Template refs
const search = ref(null);
const filterToggler = ref(null);
const leadsTable = ref(null);

const leadCustomersPerPage = computed(() => store.getters.leadCustomersPerPage);
const leadsLoading = computed(() => store.getters.leadsLoading);
const searchLeadForms = computed(() => store.getters.searchLeadForms); 
 
const leadCollectionError = computed(() => store.getters.leadCollectionError);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);

const canAccessLeads = computed(() => {
  if (isSuperAdmin.value) return true;

  // Check if user has customer_access in at least one team
  const authorizedTeams = store.getters.authorizedTeamIds('customer_access');
  return authorizedTeams === null || (Array.isArray(authorizedTeams) && authorizedTeams.length > 0);
});
const canCreateLead = computed(() => {
  if (isSuperAdmin.value) return true;
  if (selectedTeamId.value !== null) return store.getters.hasTeamPermission("customer_create", selectedTeamId.value);
  const teamIds = store.getters.authorizedTeamIds("customer_create");
  return teamIds === null || (Array.isArray(teamIds) && teamIds.length > 0);
});
const canRunLeadBulkAction = computed(() => {
  if (isSuperAdmin.value) return true;
  if (selectedTeamId.value !== null) {
    return store.getters.hasTeamPermission("customer_create", selectedTeamId.value);
  }
  const teamIds = store.getters.authorizedTeamIds("customer_create");
  return teamIds === null || (Array.isArray(teamIds) && teamIds.length > 0);
});
//
const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};

const formatDateRange = (from, to) => {
  if (!from && !to) return "";
  if (from && !to) return `${formatDate(from)} - ...`;
  if (!from && to) return `... - ${formatDate(to)}`;
  return `${formatDate(from)} - ${formatDate(to)}`;
};

const formatRange = (from, to) => {
  if (!from && !to) return "";
  if (from && !to) return `${from} - ...`;
  if (!from && to) return `... - ${to}`;
  return `${from} - ${to}`;
};

const getTeamDisplayName = (teamId) => {
  const resolvedTeamId = Number(teamId);
  if (!Number.isInteger(resolvedTeamId) || resolvedTeamId <= 0) {
    return 'All authorized teams';
  }

  const teamNameFromMembership = store.getters.membershipFor(resolvedTeamId)?.team?.name;
  if (teamNameFromMembership) return teamNameFromMembership;

  const teamNameFromAuthorizationMembership = Array.isArray(store.getters.authorizationMemberships)
    ? store.getters.authorizationMemberships.find((membership) => Number(membership?.team?.id) === resolvedTeamId)?.team?.name
    : null;
  if (teamNameFromAuthorizationMembership) return teamNameFromAuthorizationMembership;

  const teamNameFromList = Array.isArray(store.getters.teams)
    ? store.getters.teams.find((team) => Number(team?.id) === resolvedTeamId)?.name
    : null;
  if (teamNameFromList) return teamNameFromList;

  const userTeamName = Array.isArray(store.getters.loggedUser?.teams)
    ? store.getters.loggedUser.teams.find((team) => Number(team?.id) === resolvedTeamId)?.name
    : null;
  if (userTeamName) return userTeamName;

  const currentTeamId = Number(store.getters.currentTeamId);
  if (Number.isInteger(currentTeamId) && currentTeamId > 0 && currentTeamId === resolvedTeamId) {
    return store.getters.membershipFor(currentTeamId)?.team?.name || `Team ${resolvedTeamId}`;
  }

  return `Team ${resolvedTeamId}`;
};

const loadFiltersFromQuery = (q) => {
  searchKeywords.value = q.search || "";
  zipFrom.value = q.zip_from || "";
  zipTo.value = q.zip_to || "";

  const normalizedTeamIds = Array.isArray(q.teams)
    ? q.teams
    : typeof q.teams === 'string' && q.teams.length
      ? q.teams.split(',')
      : [];

  selectedTeamIds.value = normalizedTeamIds
    .flatMap((teamId) => String(teamId).split(','))
    .map((teamId) => Number(teamId))
    .filter((teamId) => Number.isInteger(teamId) && teamId > 0);

  if (q.agents) {
    selectedAgents.value = typeof q.agents === "string" ? q.agents.split(",") : q.agents;
  } else {
    selectedAgents.value = [];
  }

  leadStatuses.value = q.lead_statuses
    ? typeof q.lead_statuses === "string"
      ? q.lead_statuses.split(",").map(Number)
      : q.lead_statuses
    : [];

  leadCategories.value = q.lead_categories
    ? typeof q.lead_categories === "string"
      ? q.lead_categories.split(",").map(Number)
      : q.lead_categories
    : [];

  leadReferrals.value = q.lead_referrals
    ? typeof q.lead_referrals === "string"
      ? q.lead_referrals.split(",").map(Number)
      : q.lead_referrals
    : [];

  deadlineFrom.value = q.deadline_from || "";
  deadlineTo.value = q.deadline_to || "";
};

const searchLeads = async () => {
  const page = Number(route.query.page) || 1;

  await store.dispatch("searchLeads", {
    search: searchKeywords.value,
    agents: selectedAgents.value,
    lead_statuses: leadStatuses.value,
    lead_categories: leadCategories.value,
    lead_referrals: leadReferrals.value,
    deadline_from: deadlineFrom.value,
    deadline_to: deadlineTo.value,
    zip_from: zipFrom.value,
    zip_to: zipTo.value,
    teamId: selectedTeamIds.value.length > 0 ? selectedTeamIds.value : null,
    page,
  });

};

const updateQuery = () => {
  router
    .replace({
      query: {
        page: 1,
        search: searchKeywords.value || undefined,
        teams: selectedTeamIds.value.length > 0 ? selectedTeamIds.value : undefined,
        agents: selectedAgents.value.length ? selectedAgents.value.join(",") : undefined,
        lead_statuses: leadStatuses.value.length ? leadStatuses.value.join(",") : undefined,
        lead_categories: leadCategories.value.length ? leadCategories.value.join(",") : undefined,
        lead_referrals: leadReferrals.value.length ? leadReferrals.value.join(",") : undefined,
        deadline_from: deadlineFrom.value || undefined,
        deadline_to: deadlineTo.value || undefined,
        zip_from: zipFrom.value || undefined,
        zip_to: zipTo.value || undefined,
      },
    })
    .catch(() => {});
};

const getResults = (paginationPage) => {
  if (Number(route.query.page) === Number(paginationPage)) return;

  router
    .replace({
      query: { ...route.query, page: paginationPage },
    })
    .catch(() => {});
};

const clearDeadline = () => {
  deadlineFrom.value = "";
  deadlineTo.value = "";
  updateQuery();
};

const clearZip = () => {
  zipFrom.value = "";
  zipTo.value = "";
  updateQuery();
};

const clearAllDependentFilters = () => {
  selectedAgents.value = [];
  leadStatuses.value = [];
  leadCategories.value = [];
  leadReferrals.value = [];
  deadlineFrom.value = "";
  deadlineTo.value = "";
  zipFrom.value = "";
  zipTo.value = "";

  store.commit("setSearchLeadForms", {});
};

const removeTeam = (teamId) => {
  selectedTeamIds.value = selectedTeamIds.value.filter(id => id !== teamId);
  if (selectedTeamIds.value.length === 0) {
    clearAllDependentFilters();
  }
  updateQuery();
};

const removeAgent = (id) => {
  selectedAgents.value = selectedAgents.value.filter((x) => x !== id);
  updateQuery();
};

const removeLeadStatus = (id) => {
  leadStatuses.value = leadStatuses.value.filter((x) => x !== id);
  updateQuery();
};

const removeLeadCategory = (id) => {
  leadCategories.value = leadCategories.value.filter((x) => x !== id);
  updateQuery();
};

const removeLeadReferral = (id) => {
  leadReferrals.value = leadReferrals.value.filter((x) => x !== id);
  updateQuery();
};

const removeSearch = () => {
  searchKeywords.value = "";
  searchPlaceholder.value = "";
  updateQuery();
};

const handleSelectionChange = (selectedIds) => {
  selectedCustomerIds.value = selectedIds.map((id) => parseInt(id, 10));
};

const openTransferModal = () => {
  if (selectedCustomerIds.value.length === 0) {
    return;
  }
  store.commit("setLeadsToTransfer", selectedCustomerIds.value);
  store.commit("setShowTransferLeadsModal", true);

  selectedCustomerIds.value = [];
  
  // Clear table selection
  if (leadsTable.value?.clearSelection) {
    leadsTable.value.clearSelection();
  }
};

const showLeadImportModal = () => {
  store.commit("setShowLeadImportModal", true);
};

const showCustomerLeadModal = () => {
  if (!canCreateLead.value) {
    return;
  }

  store.commit("setShowCustomerLeadModal", true);
};

// Watch route query changes - FIX: prevent recursive updates
//let isUpdatingQuery = false;

// watch(
//   () => route.query,
//   async (query) => {
//     // Prevent recursive updates
//     if (isUpdatingQuery) {
//       isUpdatingQuery = false;
//       return;
//     }

//     if (!query.page) {
//       isUpdatingQuery = true;
//       router.replace({ query: { ...query, page: 1 } });
//       return;
//     }
    
//     loadFiltersFromQuery(query);
//     await searchLeads();
//   },
//   { immediate: true }
// );

watch(
  () => route.query,
  async (query) => {
    if (!query.page) {
      await router.replace({
        query: {
          ...query,
          page: 1,
        },
      });

      return;
    }

    loadFiltersFromQuery(query);

    await searchLeads();
  },
  { immediate: true }
);

watch(
  () => selectedTeamId.value,
  async (teamId) => {
    if (teamId !== null) {
      await store.dispatch("getSearchLeadForms", teamId);
    } else {
      store.commit("setSearchLeadForms", {});
    }
  },
  {}
);

// Lifecycle
onMounted(async () => {
  // Page-level access control
  if (!canAccessLeads.value) {
    router.replace("/dashboard");
    return;
  }

  if (store.getters.isSuperAdmin && (!Array.isArray(store.getters.teams) || store.getters.teams.length === 0)) {
    await store.dispatch("getTeams");
  }
});
</script>

<style scoped lang="scss">
.add-button {
  width: 70px !important;
  flex: initial !important;
  .button__icon {
    margin: auto;
    width: 24px;
    height: 24px;
    background-image: url("../assets/icons/default/add_icon.svg");
    background-repeat: no-repeat;
    background-position: center;
    transition: all 0.2s ease-in-out;
  }
}

.import-button{
  width: 150px !important;
}

.transfer-button{
  width: 150px !important;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.customers {
  height: 100%;

  &__content {
    margin-bottom: 20px;
    height: calc(100% - 48px - 15px - 16px - 20px);
  }
}

@media (max-width: 640px) {
  .page__right .button {
    transform: initial !important;
    margin-right: 0 !important;
    width: 100% !important;
  }
}
</style>
